/**
 * IndexedDB Helper for Wizard State Persistence
 *
 * Provides a simple key-value storage interface using IndexedDB
 * Used as a fallback when sessionStorage quota is exceeded
 */

const DB_NAME = "wizard_state_db"
const STORE_NAME = "wizard_states"
const DB_VERSION = 1

/**
 * Initialize IndexedDB database
 * Creates the object store if it doesn't exist
 */
function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

/**
 * Save data to IndexedDB
 * Used as fallback when sessionStorage quota is exceeded
 */
export async function saveToIndexedDB(key: string, value: any): Promise<void> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.put(value, key)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Get data from IndexedDB
 * Returns null if key doesn't exist
 */
export async function getFromIndexedDB(key: string): Promise<any> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readonly")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(key)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Delete data from IndexedDB
 */
export async function deleteFromIndexedDB(key: string): Promise<void> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.delete(key)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}
